/** TanStack Query wrapper for a task's figures. */

import { asFileRow } from "./use-files";
import { useCallback, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  FigureListResponse,
  FigureBriefRunResponse,
  FigureResponse,
  FileSource,
  TaskResponse,
} from "@patent-studio/api-types";
import { uploadFile } from "@/services/files";
import { describeFigure, listFigures } from "@/services/figures";
import { figuresKey, taskDetailKey } from "./query-keys";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/**
 * A figure the server has not confirmed yet.
 *
 * Marked rather than inferred from the identifier, so a row cannot be treated
 * as real because its id happened to look like one.
 */
export interface PendingFigure extends FigureResponse {
  pending: true;
}

export function isPending(figure: FigureResponse): boolean {
  return Boolean((figure as Partial<PendingFigure>).pending);
}

/**
 * The list a screen draws: what the server has, then what is still going up.
 *
 * A placeholder takes the server's identifier as soon as the upload answers,
 * and is dropped only once the refetch that replaces it has landed. In between,
 * the same figure is in both lists, and drawing it from both puts it on screen
 * twice under one React key. Here rather than on either screen, because both
 * merge the same two lists and a rule kept in two places drifts.
 */
export function mergeFigures(
  served: readonly FigureResponse[] | undefined,
  pending: readonly PendingFigure[],
): FigureResponse[] {
  const arrived = new Set((served ?? []).map((f) => f.file_id));
  return [
    ...(served ?? []),
    ...pending.filter((row) => !arrived.has(row.file_id)),
  ];
}

/** What a figure's card knows of Auto-describe: whether Praxis is writing, why it last failed, and the ask. */
export interface FigureBrief {
  describing: boolean;
  failure: string | null;
  describe: () => void;
}

/** How often the figures are read again while Praxis is describing a figure. */
export const BRIEF_POLL_MS = 2000;

/** Whether, and how often, the figures read is asked again: while an Auto-describe run is in flight. */
export function figuresPollMs(
  listing: FigureListResponse | undefined,
): number | false {
  return listing?.brief_run?.status === "running" ? BRIEF_POLL_MS : false;
}

export function useFigures(taskId: string | undefined) {
  return useQuery({
    queryKey: figuresKey(taskId ?? ""),
    queryFn: () => listFigures(taskId as string),
    enabled: taskId !== undefined,
    refetchInterval: (query) => figuresPollMs(query.state.data),
    // A hidden tab asks too, so a description that lands there is shown on return.
    refetchIntervalInBackground: true,
  });
}

/**
 * Auto-describe for one figure: whether Praxis is writing its description, why its last run wrote
 * nothing, and the ask. The run is the draft's, served on the figures read; what it writes arrives
 * in the definitions that read serves, so the card shows it as it shows any stored description.
 */
export function useFigureBrief(
  taskId: string | undefined,
  fileId: string | undefined,
  run: FigureBriefRunResponse | undefined,
): FigureBrief {
  const qc = useQueryClient();
  const ask = useMutation({
    mutationKey: MUTATION_KEYS.figureBrief,
    mutationFn: ({ task, file }: { task: string; file: string }) =>
      describeFigure(task, file),
    onSuccess: async (stamped, { task }) => {
      // A read sent before the ask landed would answer with no run in flight and stop the poll.
      await qc.cancelQueries({ queryKey: figuresKey(task) });
      qc.setQueryData<FigureListResponse>(figuresKey(task), (held) =>
        held === undefined ? held : { ...held, brief_run: stamped },
      );
    },
  });
  const mine =
    run !== undefined && fileId !== undefined && run.file_id === fileId;
  const fromRun = mine && run.status === "failed" ? (run.error ?? null) : null;
  // The ask's own state is the figure's it was made for: one hook serves whichever figure is
  // open, and the attorney may open another while an ask is on its way.
  const askedHere = fileId !== undefined && ask.variables?.file === fileId;
  return {
    describing:
      (askedHere && ask.isPending) || (mine && run.status === "running"),
    failure: (askedHere ? ask.error?.message : undefined) ?? fromRun,
    describe: () => {
      // Shown only for a figure on an open draft, so both are there whenever it is pressed.
      if (taskId !== undefined && fileId !== undefined) {
        ask.mutate({ task: taskId, file: fileId });
      }
    },
  };
}

/** The row that stands for a file until the server has answered for it. */
function placeholderFor(
  file: File,
  rowId: string,
  source: FileSource,
): PendingFigure {
  return {
    file_id: rowId,
    original_filename: file.name,
    content_type: file.type,
    label: null,
    // Nothing is defined on a drawing that is still going up.
    define_status: "not_started",
    thumbnail_url: URL.createObjectURL(file),
    // The placeholder claims the provenance the upload is declaring, so a
    // row does not change its mark when the server answers.
    source,
    pending: true,
  };
}

/** One file of a batch, with the row that stands for it until the server answers. */
interface QueuedFigure {
  file: File;
  rowId: string;
  row: PendingFigure;
}

/**
 * Upload figures, showing each row before the server has answered.
 *
 * Each file is its own upload, and each is tracked here rather than in the
 * figures cache. An attorney dropping a second selection while the first is
 * still going has several uploads live against one list, and in the cache they
 * cannot coexist: the refetch following any upload returns the server's
 * figures, which by definition exclude every file still uploading, so one
 * finishing erased the rows of the others. Held beside the cache, a refetch has
 * nothing of theirs to overwrite, and a failure takes down only its own row
 * instead of restoring a whole-list snapshot that predates its neighbours.
 *
 * A row is dropped only once its own refetch has landed, so the real figure
 * replaces the placeholder rather than the row vanishing in between. Until then
 * the row carries the identity the server gave it, which is what lets the
 * screen tell a placeholder apart from the figure it stands for.
 */
export function useUploadFigures(
  taskId: string | undefined,
  source: FileSource = "direct_upload",
) {
  const qc = useQueryClient();
  const key = figuresKey(taskId ?? "");
  const [rows, setRows] = useState<Record<string, PendingFigure>>({});
  const nextRowIdRef = useRef(0);

  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.figureUpload,
    mutationFn: ({ file }: QueuedFigure) =>
      uploadFile(taskId as string, file, "figure", undefined, source),

    // The row is drawn when the batch is picked, so it is on screen while the
    // files before it are still going up.
    onMutate: (queued: QueuedFigure) => queued,

    onSuccess: (response, _queued, context) => {
      // A supplied PDF is not a figure and never appears in the figures list:
      // the worker draws it into figures, which takes seconds to minutes, and
      // the file record is the only thing the screen can show meanwhile. It is
      // written into the task rather than invalidated, for the reason
      // ``useUploadFile`` gives: a refetch here lands after the status stream
      // has moved the file on and puts it back where it started.
      qc.setQueryData<TaskResponse>(taskDetailKey(taskId ?? ""), (prev) => {
        if (!prev) return prev;
        return { ...prev, files: [...prev.files, asFileRow(response)] };
      });

      // The server has named the file, so the row stops being anonymous. A
      // refetch triggered by some other upload can now return this figure and
      // be recognised as the same one rather than drawn beside it.
      setRows((prev) => {
        if (!(context.rowId in prev)) return prev;
        return {
          ...prev,
          [context.rowId]: {
            ...prev[context.rowId],
            file_id: response.file_id,
          },
        };
      });
    },

    onError: (_error, { file }) => {
      // The rollback takes the row away, which on its own is what a successful
      // upload looks like to anyone who was not watching.
      toast.error(`Could not upload ${file.name}. Please try again.`);
    },

    onSettled: async (_data, error, { file }, context) => {
      if (context === undefined) return;
      try {
        // Refetch first, drop the placeholder second. The other order leaves a
        // gap where the row was while the list is still in flight.
        await qc.invalidateQueries({ queryKey: key });
      } catch {
        // The upload finished and the list did not come back, so the row is
        // about to leave a list that cannot show what replaced it. Said here
        // because the figure is the thing that went missing, and the query's
        // own error state is not on this screen. An upload that itself failed
        // has already said so, and has nothing waiting to be shown.
        if (error === null) {
          toast.error(
            `Uploaded ${file.name}, but the figure list could not be refreshed. Reload the page to see it.`,
          );
        }
      }
      // A refetch that never landed is still an upload that finished. Leaving
      // the row in place would strand it on the screen and hold the preview's
      // image data for as long as the page lives.
      URL.revokeObjectURL(context.row.thumbnail_url);
      setRows((prev) => {
        const { [context.rowId]: _settled, ...rest } = prev;
        return rest;
      });
    },
  });

  const { mutateAsync } = mutation;
  // Dropping several files is one action to the attorney and one upload each to
  // the server: a single request carrying all of them would make the slowest
  // file decide when any of them appears. The server numbers a figure when its
  // upload lands, so the uploads go one after another in the order picked, and
  // the figures are numbered in that order rather than in the order they finish.
  const upload = useCallback(
    (files: File[]) => {
      // Minted per row rather than per position, so two files of the same name
      // dropped at the same moment cannot mint the same id and have React treat
      // one row as the other.
      const batch = files.map((file) => {
        const rowId = `pending-${String(nextRowIdRef.current++)}`;
        return { file, rowId, row: placeholderFor(file, rowId, source) };
      });
      setRows((prev) => ({
        ...prev,
        ...Object.fromEntries(batch.map(({ rowId, row }) => [rowId, row])),
      }));
      void (async () => {
        for (const queued of batch) {
          // A failure is reported by the mutation and ends only its own row.
          await mutateAsync(queued).catch(() => undefined);
        }
      })();
    },
    [mutateAsync, source],
  );

  return { ...mutation, upload, pending: Object.values(rows) };
}
